import { useCallback, useEffect } from "react";

import log from "logbeacon";
import { pubsub } from "utils";
import { pubSubKeyMap } from "../utils/pubSubKeyMap";

/**
 * 登录成功处理器接口
 */
interface LoginSuccessHandler {
  id: string;
  handler: () => Promise<void> | void;
}

// 移除复杂的上下文，业务方不需要关心这些

/**
 * 全局登录成功管理器
 * 负责统一管理所有页面的登录成功处理逻辑
 */
class GlobalLoginSuccessManager {
  private static instance: GlobalLoginSuccessManager;
  private handlers: Map<string, LoginSuccessHandler> = new Map();
  private isInitialized = false;
  private visibilityChangeListener: (() => void) | null = null;
  private isPageVisible = true;
  private loginSuccessListener: (() => void) | null = null;

  static getInstance() {
    if (!this.instance) {
      this.instance = new GlobalLoginSuccessManager();
    }
    return this.instance;
  }

  /**
   * 初始化全局监听器
   */
  init() {
    if (this.isInitialized) {
      return;
    }

    // 监听页面可见性变化
    this.visibilityChangeListener = () => {
      this.isPageVisible = !document.hidden;
      log.debug(
        `[GlobalLoginSuccessManager] Page visibility changed: ${this.isPageVisible}`,
      );
    };
    document.addEventListener(
      "visibilitychange",
      this.visibilityChangeListener,
    );

    // 全局登录成功监听
    this.loginSuccessListener = async () => {
      log.debug(`[GlobalLoginSuccessManager] Login success event received`, {
        handlerCount: this.handlers.size,
        isPageVisible: this.isPageVisible,
      });

      // 页面可见性检查
      if (!this.isPageVisible) {
        log.debug(`[GlobalLoginSuccessManager] Skipped: page not visible`);
        return;
      }

      // 只执行当前存在的处理器（按注册顺序）
      const handlers = Array.from(this.handlers.values());

      log.debug(
        `[GlobalLoginSuccessManager] Executing ${handlers.length} active handlers`,
      );

      // 按注册顺序执行（Map 保持插入顺序）
      for (const handler of handlers) {
        try {
          await handler.handler();
        } catch (error) {
          log.error(
            `[GlobalLoginSuccessManager] Handler ${handler.id} failed:`,
            error,
          );
        }
      }
    };

    pubsub?.on(pubSubKeyMap.LOGIN_SUCCESS, this.loginSuccessListener);

    this.isInitialized = true;
    log.info("[GlobalLoginSuccessManager] Initialized successfully");
  }

  /**
   * 注册处理器
   */
  register(handler: LoginSuccessHandler) {
    this.handlers.set(handler.id, handler);
    log.debug(`[GlobalLoginSuccessManager] Handler registered: ${handler.id}`);
  }

  /**
   * 注销处理器
   */
  unregister(id: string) {
    const existed = this.handlers.delete(id);
    log.debug(`[GlobalLoginSuccessManager] Handler unregistered: ${id}`, {
      existed,
    });
  }

  /**
   * 获取所有处理器状态
   */
  getStatus() {
    return {
      isInitialized: this.isInitialized,
      isPageVisible: this.isPageVisible,
      handlerCount: this.handlers.size,
      handlers: Array.from(this.handlers.keys()),
    };
  }

  /**
   * 清理资源
   */
  destroy() {
    if (this.visibilityChangeListener) {
      document.removeEventListener(
        "visibilitychange",
        this.visibilityChangeListener,
      );
      this.visibilityChangeListener = null;
    }

    if (this.loginSuccessListener) {
      // 注意：pubsub 可能没有 off 方法，这里使用 removeListener 或类似方法
      // 具体实现需要根据实际的 pubsub 库来调整
      try {
        (pubsub as any)?.off?.(
          pubSubKeyMap.LOGIN_SUCCESS,
          this.loginSuccessListener,
        );
      } catch (error) {
        log.warn(
          "[GlobalLoginSuccessManager] Failed to remove login success listener:",
          error,
        );
      }
      this.loginSuccessListener = null;
    }

    this.handlers.clear();
    this.isInitialized = false;
    log.info("[GlobalLoginSuccessManager] Destroyed");
  }
}

// 全局单例
const globalManager = GlobalLoginSuccessManager.getInstance();

/**
 * 全局登录成功管理器 Hook
 */
export const useGlobalLoginSuccessManager = () => {
  useEffect(() => {
    globalManager.init();
    return () => {
      // 不在这里清理，保持全局状态
    };
  }, []);
  // 使用 useCallback 缓存函数引用，避免每次渲染都创建新函数
  const register = useCallback((handler: LoginSuccessHandler) => {
    log.debug(`[useGlobalLoginSuccessManager] Registering handler`, handler);
    return globalManager.register(handler);
  }, []);

  const unregister = useCallback((id: string) => {
    log.debug(`[useGlobalLoginSuccessManager] Unregistering handler: ${id}`);
    return globalManager.unregister(id);
  }, []);

  const getStatus = useCallback(() => {
    return globalManager.getStatus();
  }, []);

  return {
    register: register,
    unregister: unregister,
    getStatus: getStatus,
  };
};

export type { LoginSuccessHandler };
